import React, {
    useContext, useEffect, useState
} from 'react';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Card, CardContent, CardHeader
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
    LogOut, Trash
} from 'lucide-react';
import {
    Link
} from 'react-router-dom';
import {
    handleLeaveOrganization
} from 'shared/utils/organizationUtils';
import AlertModal from 'shared/components/Alertmodal';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    organizationService
} from 'shared/api';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import ConfirmDeleteAlert from 'shared/components/ConfirmDeleteAlert';
import {
    NestedPageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import {
    CreateOrganizationFullscreen
} from 'features/organizations/CreateOrganizationFullScreen';

function Organization() {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const {
        organizations
    } = useContext(Context);

    return (
        <NestedPageLayout>
            <Card className="shadow-md flex-grow">
                <CardHeader>
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="font-bold"> Organizations </h1>
                        <div className="flex items-center gap-4">
                            <Button onClick={() => setIsCreateDialogOpen(true)} size="sm">
                                Create New Organization
                            </Button>
                            <CreateOrganizationFullscreen
                                open={isCreateDialogOpen}
                                onClose={() => setIsCreateDialogOpen(false)}
                                onComplete={() => {}}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Members</TableHead>
                                    <TableHead>Cluster Status</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {organizations.map((item, index) => (
                                    <OrganizationCard {...item} index={index} key={index} />
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </NestedPageLayout>
    );
}

const OrganizationCard = ({
    name, created, _id, members, index, clusterStatus, reason, superAdmin
}) => {

    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // leave org state
    const [LeaveLoading, setLeaveLoading] = useState(false);
    const [leaveOrgModal, setLeaveOrgModal] = useState(false);
    const {
        user, addNewNotifcation
    } = useContext(Context);

    const isSuperAdmin = superAdmin._id === user._id; // Check if the user is a superAdmin for this specific organization

    const handleLeave = async () => {
        setLeaveLoading(true);
        try {
            await handleLeaveOrganization(_id, user._id);
        } catch (error) {
            if (error.response?.status === 409) {
                addNewNotifcation('Super admin cannot leave the organization', 'warning');
            } else {
                addNewNotifcation('Something went wrong. Please try again', 'warning');
            }
        } finally {
            setLeaveLoading(false);
        }
    };

    const handleDeleteOrganization = () => {
        if (!isSuperAdmin) {
            return addNewNotifcation('Super admin can only delete the organization', 'warning');
        }

        setDeleteLoading(true);
        organizationService.deleteOrganization(_id)
            .then(() => {

                addNewNotifcation('Organization delete successfully', 'success');
                setTimeout(() => {
                    window.location = '/organizations';
                }, 1000);
            }).catch((error) => {

                if (error.response?.status === 409) {
                    addNewNotifcation('Super admin can only delete the organization', 'warning');
                } else {
                    addNewNotifcation('Something went wrong. Please try again', 'warning');
                }
            }).finally(() => {
                setDeleteLoading(false);
            });
    };

    const handleOpenModel = () => {
        if (isSuperAdmin) {
            setIsDialogOpen(true);
        } else {
            setLeaveOrgModal(true);
        }
    };

    useEffect(() => {
        let timeoutId;

        const checkStatus = async () => {
            if (clusterStatus === 'DEPLOYING') {
                const res = await checkDeploymentStatus();
                if (res) {
                    window.location.reload();
                } else {
                    timeoutId = setTimeout(checkStatus, 5000); // Wait 5 seconds before next check
                }
            }
        };

        checkStatus(); // Start the status check

        return () => clearTimeout(timeoutId); // Clear timeout on cleanup
    }, [clusterStatus]);

    const checkDeploymentStatus = async () => {
        try {
            const response = await organizationService.checkOrganizationCluster(_id);
            if (response.status === 202) {
                return false;
            } else {
                return true;
            }
        } catch (error) {
            console.error('Error checking deployment status:', error);
            return false;
        }
    };

    const membersCount = clusterStatus === 'DEPLOYED' ? (members?.length || 0) + 1 : 'N/A';

    return (
        <TableRow key={index}>
            <TableCell className='p-4'>
                <div className="flex items-center gap-2">
                    <Link className={`flex items-center gap-2 text-[blue] hover:underline ${clusterStatus === 'DEPLOYED' ? '' : 'pointer-events-none'}`} to={`/organizations/${_id}`}>
                        <span>{name}</span>
                    </Link>
                </div>
            </TableCell>
            <TableCell className='p-4'>{created.split('T')[0] || '2024/2/2'}</TableCell>
            <TableCell className='p-4'>{membersCount}</TableCell>
            <TableCell className='p-4'>
                {clusterStatus === 'DEPLOYED' ? (
                    <Badge variant="outline" className="bg-green-500 text-green-50">
                        Deployed
                    </Badge>
                ) : clusterStatus === 'DELETED' ? (
                    <Badge variant="outline" className="bg-red-500 text-green-50">
                        DELETED
                    </Badge>
                ) : clusterStatus === 'FAILED' ? (
                    <>
                        <Badge variant="outline" className="bg-red-500 text-yellow-50">
                            failed
                        </Badge>
                        <div className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {reason || 'Something went wrong'}
                        </div>
                    </>
                ) : clusterStatus === 'DELETING' ? (
                    <>
                        <Badge variant="outline" className="bg-red-500 text-yellow-50">
                            deleting
                        </Badge>
                    </>
                ) : (
                    <>
                        <Badge variant="outline" className="bg-yellow-500 text-yellow-50">
                            Deploying...
                        </Badge>
                        <div className="text-xs text-muted-foreground">
                            Organization is still being deployed and is not yet available for use.
                        </div>
                    </>
                )}
            </TableCell>
            <TableCell className='p-4'>
                <div className="flex items-center gap-2">
                    {isSuperAdmin ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <Button disabled={clusterStatus === 'DEPLOYING' || clusterStatus === 'DEPLOYED' || clusterStatus === 'DELETING'} onClick={handleOpenModel} variant="destructive" className='flex items-center gap-2 text-[13px]' size="sm">
                                            <Trash size={13} />
                                            Delete Organization
                                        </Button>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="bg-gray-900 text-white">
                                    {clusterStatus === 'DEPLOYED' && (
                                        <span> You can not delete organization with deployed cluster, you need to terminate its cluster first </span>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <Button disabled={clusterStatus === 'DEPLOYING'} onClick={handleOpenModel} variant="destructive" className='flex items-center gap-2 text-[13px]' size="sm">
                            <LogOut size={13} />
                            Leave
                        </Button>
                    )}
                </div>
            </TableCell>

            {/* leave organization alert modal */}
            <AlertModal openModal={leaveOrgModal}
                setopenModal={setLeaveOrgModal} onDelete={handleLeave} loading={LeaveLoading}
                title='Are you sure you want to leave this organization'
                subTitle='This action cannot be undone and you will lose access to all organizational resources and data.'
                btnTitle='Leave' />

            {/* delete organization alert */}
            <ConfirmDeleteAlert
                isDialogOpen={isDialogOpen}
                setIsDialogOpen={setIsDialogOpen}
                name={name}
                deleteLoading={deleteLoading}
                handleDeleteOrganization={handleDeleteOrganization} />

        </TableRow>
    );
};

export default Organization;